import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";

/**
 * Shared luxury layout for legal/policy pages. Matches Facetra's matte
 * black + soft gold aesthetic. Renders a left-aligned readable column.
 *
 * SEO: pass `noIndex` for utility pages (contact, terms, refund) that
 * shouldn't compete for crawl budget with commercial-intent pillars.
 * The robots meta is overridden at runtime so Google's JS rendering
 * picks up the noindex, follow directive.
 */
export default function LegalLayout({ title, lastUpdated, children, testId, noIndex = false }) {
  useEffect(() => {
    if (!noIndex) return undefined;
    // Override the site-wide "index, follow" default in public/index.html.
    // We inject a second robots tag; browsers respect the last one, and
    // Googlebot honours a noindex directive from any robots meta tag.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, follow";
    meta.dataset.legalRobots = "1";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [noIndex]);

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white relative overflow-hidden" data-testid={testId}>
      <div className="absolute inset-0 radial-gold-glow opacity-30 pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Top bar */}
      <header className="relative border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="legal-home-link">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.35)]">
              <span className="font-display text-black text-sm font-bold">F</span>
            </div>
            <span className="font-display text-lg tracking-tight">Facetra</span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="text-xs text-white/55 hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
        </div>
      </header>

      <main className="relative max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
          Facetra · Legal
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          {title}
        </h1>
        {lastUpdated && (
          <p className="mt-4 text-xs text-white/45">Last updated: {lastUpdated}</p>
        )}

        <div className="mt-10 prose-legal">
          {children}
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/40">
          <span>© {new Date().getFullYear()} Facetra Design Studio Private Limited</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-white/70 transition">Privacy</Link>
            <Link to="/terms" className="hover:text-white/70 transition">Terms</Link>
            <Link to="/refund" className="hover:text-white/70 transition">Refund</Link>
            <Link to="/contact" className="hover:text-white/70 transition">Contact</Link>
          </div>
        </div>
      </main>

      <style>{`
        .prose-legal h2 {
          font-family: 'Fraunces', serif;
          font-size: 1.5rem;
          letter-spacing: -0.01em;
          color: #fff;
          margin-top: 2.25rem;
          margin-bottom: 0.85rem;
        }
        .prose-legal h3 {
          font-size: 0.95rem;
          font-weight: 600;
          color: #D4AF37;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .prose-legal p, .prose-legal li {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.72);
          line-height: 1.7;
        }
        .prose-legal p { margin-bottom: 0.85rem; }
        .prose-legal ul { padding-left: 1.1rem; margin-bottom: 0.85rem; }
        .prose-legal li { margin-bottom: 0.45rem; list-style: disc; }
        .prose-legal strong { color: #fff; font-weight: 600; }
        .prose-legal a { color: #D4AF37; text-decoration: underline; text-decoration-color: rgba(212,175,55,0.4); }
        .prose-legal a:hover { color: #F3CE5A; }
        .prose-legal table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; }
        .prose-legal th, .prose-legal td { padding: 0.5rem 0.75rem; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 0.9rem; text-align: left; }
        .prose-legal th { color: #D4AF37; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; font-size: 0.75rem; }
      `}</style>
    </div>
  );
}
